import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getDoctorProfile, updateDoctorProfile } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { Loader2, Trash2, AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog"
import { supabase } from '@/lib/supabase'

interface DoctorProfile {
  first_name: string
  last_name: string
  state: string
  email: string
}

export default function Settings() {
  const { user, signOut } = useAuth()
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [profile, setProfile] = useState<DoctorProfile>({
    first_name: '',
    last_name: '',
    state: '',
    email: ''
  })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (user) {
      getDoctorProfile(user.id).then(data => {
        if (data) {
          setProfile(data)
        }
      })
    }
  }, [user])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setLoading(true)
    try {
      await updateDoctorProfile(user.id, profile)
      toast({
        title: "Perfil atualizado",
        description: "Suas informações foram atualizadas com sucesso.",
      })
    } catch (error) {
      toast({
        title: "Erro ao atualizar",
        description: "Ocorreu um erro ao atualizar suas informações.",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setProfile(prev => ({ ...prev, [name]: value }))
  }

  const handleDeleteAccount = async () => {
    setLoading(true)
    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', user?.id)

      if (error) throw error

      await signOut()
      
      toast({
        title: 'Conta deletada',
        description: 'Sua conta foi deletada com sucesso.',
      })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Não foi possível deletar sua conta.',
      })
    } finally {
      setLoading(false)
      setShowDeleteConfirm(false)
    }
  }

  return (
    <div className="container max-w-2xl py-6 sm:py-10 px-4 sm:px-6">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">Configurações</h1>
      
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="first_name" className="text-sm sm:text-base">Nome</Label>
            <Input
              id="first_name"
              name="first_name"
              value={profile.first_name}
              onChange={handleChange}
              required
              className="h-10 sm:h-12 text-sm sm:text-base"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="last_name" className="text-sm sm:text-base">Sobrenome</Label>
            <Input
              id="last_name"
              name="last_name"
              value={profile.last_name}
              onChange={handleChange}
              required
              className="h-10 sm:h-12 text-sm sm:text-base"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm sm:text-base">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={profile.email}
            onChange={handleChange}
            required
            className="h-10 sm:h-12 text-sm sm:text-base"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="state" className="text-sm sm:text-base">Estado</Label>
          <Input
            id="state"
            name="state"
            value={profile.state}
            onChange={handleChange}
            required
            className="h-10 sm:h-12 text-sm sm:text-base"
          />
        </div>

        <Button 
          type="submit" 
          disabled={loading} 
          className="w-full h-10 sm:h-12 text-sm sm:text-base mt-4"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Atualizando...
            </>
          ) : (
            'Salvar alterações'
          )}
        </Button>
      </form>

      <div className="mt-6 sm:mt-8">
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogTrigger asChild>
            <Button 
              variant="destructive" 
              className="w-full h-10 sm:h-12 text-sm sm:text-base"
            >
              <Trash2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
              Deletar Conta
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl">Deletar Conta</DialogTitle>
              <DialogDescription className="text-sm sm:text-base">
                Tem certeza que deseja deletar sua conta? Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-destructive">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
              <p className="text-xs sm:text-sm">
                Todos os seus dados serão permanentemente excluídos.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className="h-9 sm:h-10 text-sm"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={loading}
                className="h-9 sm:h-10 text-sm"
              >
                Deletar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
} 